import { useTranslation } from 'react-i18next';

import type { DraftDuration } from '@/shared/lib/deadline-draft';

import { formatCalendarDate } from '@/shared/lib/datetime';

import { DateField } from './DateField';
import styles from './RangeGroups.module.css';
import { TimeField } from './TimeField';
import { useDurationText } from './use-duration-text';

export interface RangeValue {
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
}

interface Props {
  value: RangeValue;
  onChange: (patch: Partial<RangeValue>) => void;
  /** The Start group is shown (a task has only its End). */
  showStart?: boolean;
  /** All-day / date-only editing: no time fields. */
  showTimes?: boolean;
  /** `Time (optional)` labels (deadline fields) instead of `Time`. */
  timeOptional?: boolean;
  /** Marks the controls the caller found wrong. */
  invalid?: Partial<Record<keyof RangeValue, boolean>>;
  describedBy?: string;
  disabled?: boolean;
  /** Dates that are shown but cannot be changed (a series split keeps the chosen slot's date). */
  fixedDates?: { start?: boolean; end?: boolean };
  /** Labels of the groups (default Start / End). */
  labels?: { start: string; end: string };
  /** Calculated length, shown under the groups when both endpoints are known. */
  duration?: DraftDuration | null;
}

/**
 * Start and End as two groups, each "date + time". Side by side when the container is wide enough,
 * stacked on a phone; the date and the time of one endpoint always stay together (never a date of
 * one endpoint beside the time of the other). Presentational: the caller owns the value and the
 * validation.
 */
export function RangeGroups({
  value,
  onChange,
  showStart = true,
  showTimes = true,
  timeOptional = false,
  invalid = {},
  describedBy,
  disabled = false,
  fixedDates,
  labels,
  duration,
}: Props) {
  const { t, i18n } = useTranslation();
  const length = useDurationText(duration);
  const timeLabel = timeOptional ? t('deadline.timeOptional') : t('deadline.time');
  const group = (side: 'start' | 'end') => (
    <fieldset className={styles.group} data-side={side} disabled={disabled}>
      <legend>
        {side === 'start'
          ? (labels?.start ?? t('deadline.start'))
          : (labels?.end ?? t('deadline.end'))}
      </legend>
      <div className={styles.pair} data-times={showTimes ? 'true' : 'false'}>
        {fixedDates?.[side] ? (
          <div className={styles.fixed} data-testid={`fixed-${side}-date`}>
            <span className={styles.fixedLabel}>{t('deadline.date')}</span>
            <span className={styles.fixedValue}>
              {formatCalendarDate(
                side === 'start' ? value.startDate : value.endDate,
                i18n.language,
                true,
              )}
            </span>
          </div>
        ) : (
          <DateField
            label={t('deadline.date')}
            value={side === 'start' ? value.startDate : value.endDate}
            invalid={invalid[side === 'start' ? 'startDate' : 'endDate'] === true}
            {...(describedBy && { describedBy })}
            onChange={(date) => {
              onChange(side === 'start' ? { startDate: date } : { endDate: date });
            }}
          />
        )}
        {showTimes && (
          <TimeField
            label={timeLabel}
            value={side === 'start' ? value.startTime : value.endTime}
            invalid={invalid[side === 'start' ? 'startTime' : 'endTime'] === true}
            {...(describedBy && { describedBy })}
            onChange={(time) => {
              onChange(side === 'start' ? { startTime: time } : { endTime: time });
            }}
          />
        )}
      </div>
    </fieldset>
  );
  return (
    <div className={styles.root} data-testid="range-groups">
      <div className={styles.groups} data-single={showStart ? undefined : 'true'}>
        {showStart && group('start')}
        {group('end')}
      </div>
      {length && (
        <p className={styles.duration} data-testid="range-duration">
          {t('deadline.duration.label', { value: length })}
        </p>
      )}
    </div>
  );
}
